import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../../../core/services/api/api.service';
import { QuizQuestionStudent, QuizSubmitResult } from '../../../../models/interfaces/quiz.interface';
import { QuizQuestionComponent } from './quiz-question.component';
import { QuizResultComponent } from './quiz-result.component';
import { EnfafButtonComponent } from '../../../../components/enfaf-button/enfaf-button.component';
import { EnfafIconComponent } from '../../../../components/enfaf-icon/enfaf-icon.component';

export interface QuizFlowDialogData {
  courseClassId: string;
}

export interface QuizFlowDialogResult {
  action: 'postpone' | 'next-class' | 'rewatch';
  result?: QuizSubmitResult;
}

@Component({
  selector: 'app-quiz-flow',
  standalone: true,
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    QuizQuestionComponent,
    QuizResultComponent,
    EnfafButtonComponent,
    EnfafIconComponent
  ],
  templateUrl: './quiz-flow.component.html',
  styleUrl: './quiz-flow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuizFlowComponent {
  private api = inject(ApiService);
  private dialogRef = inject(MatDialogRef<QuizFlowComponent>);
  private data: QuizFlowDialogData = inject(MAT_DIALOG_DATA);

  step = signal<'invitation' | 'loading' | 'question' | 'result'>('invitation');
  questions = signal<QuizQuestionStudent[]>([]);
  currentQuestionIndex = signal(0);
  /** Single source of truth for all answers: questionId → selectedOptionIndex */
  answersByQuestionId = signal<Record<string, number>>({});
  result = signal<QuizSubmitResult | null>(null);
  isSubmitting = signal(false);
  isStarting = signal(false);
  errorMessage = signal<string | null>(null);

  currentQuestion = computed(() => this.questions()[this.currentQuestionIndex()] ?? null);
  totalQuestions = computed(() => this.questions().length);
  /** Derives the stored answer for the currently displayed question. */
  currentAnswer = computed(() => {
    const q = this.currentQuestion();
    if (!q) return null;
    const val = this.answersByQuestionId()[q.quizQuestionId];
    return val !== undefined ? val : null;
  });

  onStartQuiz(): void {
    if (this.isStarting()) return;
    this.isStarting.set(true);
    this.step.set('loading');
    this.loadQuiz();
  }

  onPostpone(): void {
    this.dialogRef.close({ action: 'postpone' } as QuizFlowDialogResult);
  }

  onQuestionAnswered(payload: { quizQuestionId: string; selectedOptionIndex: number }): void {
    this.answersByQuestionId.update((prev) => ({
      ...prev,
      [payload.quizQuestionId]: payload.selectedOptionIndex
    }));

    const nextIndex = this.currentQuestionIndex() + 1;
    if (nextIndex < this.questions().length) {
      this.currentQuestionIndex.set(nextIndex);
    } else {
      this.submitQuiz();
    }
  }

  onGoBack(): void {
    const idx = this.currentQuestionIndex();
    if (idx === 0) {
      this.step.set('invitation');
      this.answersByQuestionId.set({});
    } else {
      this.currentQuestionIndex.set(idx - 1);
    }
  }

  onResultAction(action: 'next-class' | 'rewatch'): void {
    this.dialogRef.close({ action, result: this.result() ?? undefined } as QuizFlowDialogResult);
  }

  private async loadQuiz(): Promise<void> {
    try {
      const response = await firstValueFrom(this.api.startClassQuiz(this.data.courseClassId));
      this.questions.set(response.questions);
      this.answersByQuestionId.set({});
      this.currentQuestionIndex.set(0);
      this.step.set('question');
    } catch {
      this.errorMessage.set('Error al cargar la autoevaluación');
      this.step.set('invitation');
    } finally {
      this.isStarting.set(false);
    }
  }

  private async submitQuiz(): Promise<void> {
    this.isSubmitting.set(true);
    try {
      const answers = this.questions().map((q) => ({
        quizQuestionId: q.quizQuestionId,
        selectedOptionIndex: this.answersByQuestionId()[q.quizQuestionId] ?? 0
      }));
      const res = await firstValueFrom(this.api.submitClassQuiz(this.data.courseClassId, answers));
      this.result.set(res);
      this.step.set('result');
    } catch {
      this.errorMessage.set('Error al enviar las respuestas');
      this.step.set('invitation');
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
